import { z } from 'zod';
/** Environments the applications understand without implying a production activation. */
export const deploymentTiers = ['development', 'preview', 'production'];
export const deploymentTierSchema = z.enum(deploymentTiers);
/**
 * Exact hosts development, preview, and automated traffic must never target.
 *
 * Kept in the shared browser-safe contract package so frontend guards do not duplicate these
 * literals and repository hostname policy remains closed over one source.
 */
export const prohibitedAutomationHosts = ['apuestapr.com', 'register.apuestapr.com'];
export const prohibitedAutomationHostSchema = z.enum(prohibitedAutomationHosts);
/**
 * Whether a hostname is one of the prohibited ones — Task 15.3.
 *
 * **Exact, and deliberately not a suffix match.** `apuestapr.com` is prohibited;
 * `web-preview.apuestapr.com` is not, and neither is `dashboard.apuestapr.com`, which is a permitted
 * content-image origin. A suffix rule would forbid the preview environments this programme runs in and
 * the image host the contracts allow, so it would be turned off within a day — and a guard that gets
 * turned off protects nothing. What the owner prohibited is traffic to the live apex and the live
 * registration host, which is exactly these two names.
 *
 * The comparison lowercases and strips a trailing dot, because `APUESTAPR.COM` and `apuestapr.com.` are
 * the same host to DNS and to a browser. It does not strip a port: a hostname has none, and a caller
 * with a `host:port` string should be asking about the URL instead — see `prohibitedAutomationTarget`.
 */
export const isProhibitedAutomationHost = (hostname) => {
    const canonical = hostname.trim().toLowerCase().replace(/\.$/, '');
    return prohibitedAutomationHosts.includes(canonical);
};
/**
 * The prohibited host a URL targets, or `undefined` when it targets none.
 *
 * Returns the host rather than a boolean so a caller's failure message can name what it found. A value
 * that is not a URL at all returns `undefined`: this function answers "does this point at a forbidden
 * host", and "this is not a URL" is a different complaint for a different check to make.
 */
export const prohibitedAutomationTarget = (url) => {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return undefined;
    }
    return isProhibitedAutomationHost(parsed.hostname) ? parsed.hostname : undefined;
};
/**
 * Reserved destinations for the CTA links, for use wherever a real one must not be reached.
 *
 * `.test` is reserved by RFC 6761 and resolves nowhere, so a run configured with these cannot reach the
 * live registration or play destination even if something clicked one. They live here rather than in a
 * test file because both the smoke harness and the CSP harness need the same values, and because a
 * reserved destination is a deployment-configuration fact rather than a fixture.
 */
export const reservedCtaDestinations = Object.freeze({
    registration: 'https://registration.example.test/start',
    play: 'https://play.example.test/',
});
//# sourceMappingURL=deployment.js.map